/**
 * # handSeedMinter (T9b — writer-side handle minting off HEADER `$HANDSEED`)
 *
 * The writer synthesizes a few records on its own (an APPID table record per
 * custom XData appid, an anonymous `*D<n>` dimension block stub). R13+ requires
 * EVERY such record to carry its own group-5 handle — a handle-less record is
 * silently re-minted by ezdxf on load (no audit fix record) and rejected /
 * recovered by AutoCAD. The DXF-native source of fresh handles is the HEADER
 * `$HANDSEED` variable ("next available object handle", Autodesk DXF reference
 * HEADER section; ezdxf `EntityDB.next_handle` is seeded from it), so the writer
 * mints `seed, seed+1, …` and writes the advanced seed back — exactly what
 * AutoCAD does when it creates an object.
 *
 * COLLISION SAFETY (T9b review P2) — a real-world IMPORTED file can carry a
 * STALE `$HANDSEED` that sits BELOW its own maximum handle (ezdxf `Drawing.load`
 * resets its counter from `$HANDSEED` verbatim, so such files exist). Minting
 * strictly from the seed would then hand out a handle an existing entity /
 * record already owns — worse than the old handle-less shape. The minter
 * therefore starts at `max($HANDSEED, maxHandleInDocument + 1)`, where the
 * document maximum is gathered LAZILY (on the first `mint()`, i.e. only when
 * there is actually something to mint) by walking the DxfDocument object graph:
 * every `handle`-suffixed string field (`handle`, `endblkHandle`,
 * `seqendHandle`, `ownerHandle`, `layoutHandle`, …), every string member of a
 * `*Handles` array, and every raw `{code: 5|105, value}` group. Collecting
 * references as well as own handles over-approximates the used set, which only
 * pushes the seed higher — never a collision. The committed `$HANDSEED` is the
 * value after the last mint, so it sits strictly above every minted handle.
 *
 * A document WITHOUT a HEADER `$HANDSEED` (a bare in-memory / R12-shaped
 * fixture) has no handle space to mint from: the minter is `undefined` and the
 * synthesized records keep their legacy handle-less shape (byte-parity for those
 * callers is unchanged). Pure: the input sections are never mutated — `commit`
 * returns a new section list with a cloned HEADER.
 *
 * ARITHMETIC (T9b review round 1) — a DXF handle is an arbitrary-length hex
 * string (Autodesk: "up to 16 hexadecimal digits"; ezdxf stores it as `int`).
 * JS `number` loses integer precision at 2^53 (`0x20000000000000`): above it
 * `next += 1` stalls on the same value and two mints would return the SAME
 * handle — a duplicate group-5. The seed parse, the document maximum, the
 * increment and the hex formatting therefore run on `bigint` end to end.
 */
import type { DxfGroup, DxfSection } from "./types";

/** A `$HANDSEED`-backed minter for records the writer synthesizes. */
export interface HandSeedMinter {
  /** Next fresh uppercase-hex handle (advances the seed). */
  mint(): string;
  /**
   * Return `sections` with the HEADER `$HANDSEED` advanced past every minted
   * handle (a cloned HEADER section; the input is untouched). Identity when
   * nothing was minted.
   */
  commit(sections: readonly DxfSection[]): DxfSection[];
}

const HEX_HANDLE_RE = /^[0-9A-Fa-f]+$/;
const OWN_HANDLE_GROUP_CODES = new Set<number>([5, 105]);

/** Locate the `5 <hex>` value group that follows `9 $HANDSEED`. */
function findHandSeedIndex(groups: readonly DxfGroup[]): number {
  for (let i = 0; i < groups.length - 1; i += 1) {
    const g = groups[i];
    if (g && g.code === 9 && g.value.trim() === "$HANDSEED") return i + 1;
  }
  return -1;
}

/** Parse a hex handle string to `bigint`; `undefined` for a non-hex value. */
function parseHexHandle(value: unknown): bigint | undefined {
  if (typeof value !== "string" || !HEX_HANDLE_RE.test(value)) return undefined;
  return BigInt(`0x${value}`);
}

/** Uppercase-hex DXF spelling of a `bigint` handle (no leading zeros). */
export function formatHexHandle(value: bigint): string {
  return value.toString(16).toUpperCase();
}

/**
 * `bigint` maximum of a set of hex handle strings (non-hex / empty entries
 * ignored; `0n` for an empty set). The exact counterpart of the `number`-based
 * census maximum for callers that mint handles — a handle at or above 2^53
 * is compared exactly instead of rounded.
 */
export function maxHexHandleValueBig(handles: Iterable<string>): bigint {
  let max = 0n;
  for (const h of handles) {
    const n = parseHexHandle(h);
    if (n !== undefined && n > max) max = n;
  }
  return max;
}

/** A collision-checked `bigint` hex-handle minter (see {@link createHexHandleMinter}). */
export interface HexHandleMinter {
  /** Next fresh uppercase-hex handle strictly above every earlier mint. */
  mint(): string;
  /** The `bigint` value the NEXT `mint()` would start probing from. */
  next(): bigint;
}

/**
 * The ONE `bigint` hex-handle counter every minter builds on (T9b review
 * round 3): starts strictly above `floor`, skips any value `isUsed` reports
 * as taken (lowercased hex — DXF handles are case-insensitive) and every
 * handle it minted itself, and formats with {@link formatHexHandle}. `number`
 * arithmetic stalls at 2^53 (`seq += 1` yields the same value) and a
 * collision loop built on it never terminates — `bigint` end to end here so
 * no caller has to reimplement the increment.
 */
export function createHexHandleMinter(
  floor: bigint,
  isUsed: (lowerHex: string) => boolean = () => false,
): HexHandleMinter {
  let next = floor + 1n;
  const minted = new Set<string>();
  return {
    mint(): string {
      let handle = formatHexHandle(next);
      while (isUsed(handle.toLowerCase()) || minted.has(handle.toLowerCase())) {
        next += 1n;
        handle = formatHexHandle(next);
      }
      minted.add(handle.toLowerCase());
      next += 1n;
      return handle;
    },
    next(): bigint {
      return next;
    },
  };
}

/**
 * Numeric maximum of every handle-valued string reachable from `node`: a
 * `handle` / `*Handle` string field, a `*Handles` string array, or a raw
 * `{code: 5|105, value}` group. Non-hex values are ignored. The walk is
 * shape-agnostic on purpose — a new handle-bearing field on any entity /
 * record type is picked up without touching this module. `bigint` so a
 * handle above 2^53 is compared exactly (see the module doc).
 */
export function maxHandleInDocument(node: unknown): bigint {
  let max = 0n;
  const note = (value: unknown): void => {
    const n = parseHexHandle(value);
    if (n !== undefined && n > max) max = n;
  };
  const seen = new Set<object>();
  const walk = (n: unknown): void => {
    if (n === null || typeof n !== "object") return;
    if (seen.has(n)) return;
    seen.add(n);
    if (Array.isArray(n)) {
      // The HEADER `9 $HANDSEED / 5 <seed>` pair: the seed's own `5` group is
      // NOT an object handle — counting it would bump the minter past the seed
      // on every save (churn). Skip the `5` that directly follows the name.
      let afterSeedName = false;
      for (const item of n) {
        const g = item as { code?: unknown; value?: unknown } | null;
        const isSeedValue = afterSeedName && g?.code === 5;
        afterSeedName =
          g?.code === 9 &&
          typeof g.value === "string" &&
          g.value.trim() === "$HANDSEED";
        if (!isSeedValue) walk(item);
      }
      return;
    }
    const record = n as Record<string, unknown>;
    if (
      typeof record.code === "number" &&
      OWN_HANDLE_GROUP_CODES.has(record.code) &&
      typeof record.value === "string"
    ) {
      note(record.value);
      return;
    }
    for (const [key, value] of Object.entries(record)) {
      if (typeof value === "string") {
        if (/handle$/i.test(key)) note(value);
        continue;
      }
      if (Array.isArray(value) && /handles$/i.test(key)) {
        for (const item of value) note(item);
      }
      walk(value);
    }
  };
  walk(node);
  return max;
}

/**
 * Build a {@link HandSeedMinter} from the document's HEADER `$HANDSEED`, or
 * `undefined` when the document carries no HEADER / no `$HANDSEED` / a non-hex
 * seed (nothing to mint from — never fabricate a handle space).
 */
export function createHandSeedMinter(
  sections: readonly DxfSection[],
): HandSeedMinter | undefined {
  const header = sections.find(
    (s) => s.kind === "raw-section" && s.name === "HEADER",
  );
  if (!header || header.kind !== "raw-section") return undefined;
  const seedIndex = findHandSeedIndex(header.groups);
  if (seedIndex < 0) return undefined;
  const seedValue = header.groups[seedIndex]?.value.trim() ?? "";
  const headerSeed = parseHexHandle(seedValue);
  if (headerSeed === undefined || headerSeed <= 0n) return undefined;
  // Resolved on the first mint (see the module doc): the document walk is only
  // paid when the writer actually has something to synthesize. The counter
  // itself is the shared `bigint` minter (`createHexHandleMinter`), started at
  // `max($HANDSEED, documentMax + 1)`; the seed already sits above every
  // handle by construction, so no per-mint collision probe is needed.
  let counter: HexHandleMinter | undefined;
  let minted = 0;
  return {
    mint(): string {
      if (counter === undefined) {
        const documentMax = maxHandleInDocument(sections) + 1n;
        const start = headerSeed > documentMax ? headerSeed : documentMax;
        counter = createHexHandleMinter(start - 1n);
      }
      minted += 1;
      return counter.mint();
    },
    commit(input: readonly DxfSection[]): DxfSection[] {
      if (minted === 0 || counter === undefined) return [...input];
      const committed = formatHexHandle(counter.next());
      return input.map((section) => {
        if (section.kind !== "raw-section" || section.name !== "HEADER") {
          return section;
        }
        const index = findHandSeedIndex(section.groups);
        if (index < 0) return section;
        const groups = section.groups.map((g) => ({ ...g }));
        groups[index] = { code: 5, value: committed };
        return { ...section, groups };
      });
    },
  };
}
